import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantsAPI } from '../../services/api';
import { formatDate, formatRelativeDate } from '../../utils/dateFormat';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import './Admin.css';

interface Tenant {
  id: number;
  name: string;
  subdomain: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  logo_url: string | null;
  created_at: string;
  user_count?: number;
  is_active?: boolean;
  features?: Record<string, boolean>;
}

interface TenantMember {
  id: string;
  name: string;
  email: string;
  role: string;
  user_type: string;
  is_active: boolean;
  last_login: string | null;
  last_seen: string | null;
  engagement: 'high' | 'medium' | 'low' | 'none';
  days_remaining: number | null;
}

interface TenantFormData {
  name: string;
  subdomain: string;
  primary_color: string;
  secondary_color: string;
  website_url: string;
}

interface HarvestedColors {
  primary: string;
  secondary: string;
  accent: string;
  logo_url?: string;
}

const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    subdomain: '',
    primary_color: '#2563eb',
    secondary_color: '#10b981',
    website_url: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [harvesting, setHarvesting] = useState(false);
  const [harvestedColors, setHarvestedColors] = useState<HarvestedColors | null>(null);

  // Module visibility toggles (edit mode only)
  const [moduleToggles, setModuleToggles] = useState<Record<string, boolean>>({});

  // Delete confirmation
  const [deleteTenant, setDeleteTenant] = useState<Tenant | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Activate/deactivate confirmation
  const [toggleActiveTenant, setToggleActiveTenant] = useState<Tenant | null>(null);

  // Expandable tree view
  const [expandedTenant, setExpandedTenant] = useState<number | null>(null);
  const [tenantMembers, setTenantMembers] = useState<TenantMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tenantsAPI.list();
      setTenants(response.data.tenants || response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const openCreateModal = () => {
    setModalMode('create');
    setEditingTenant(null);
    setFormData({
      name: '',
      subdomain: '',
      primary_color: '#2563eb',
      secondary_color: '#10b981',
      website_url: '',
    });
    setFormError(null);
    setHarvestedColors(null);
    setShowModal(true);
  };

  const openEditModal = (tenant: Tenant) => {
    setModalMode('edit');
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      subdomain: tenant.subdomain || '',
      primary_color: tenant.primary_color || '#2563eb',
      secondary_color: tenant.secondary_color || '#10b981',
      website_url: '',
    });
    setModuleToggles(tenant.features || {});
    setFormError(null);
    setHarvestedColors(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTenant(null);
    setFormError(null);
    setHarvestedColors(null);
  };

  const handleHarvestColors = async () => {
    if (!formData.website_url) {
      setFormError('Please enter a website URL');
      return;
    }

    setHarvesting(true);
    setFormError(null);

    try {
      const response = await tenantsAPI.harvestColors(formData.website_url);
      const colors = response.data;
      setHarvestedColors(colors);
      // Auto-fill the color fields
      if (colors.primary) setFormData(f => ({ ...f, primary_color: colors.primary }));
      if (colors.secondary) setFormData(f => ({ ...f, secondary_color: colors.secondary }));
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to harvest colors');
    } finally {
      setHarvesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      if (modalMode === 'create') {
        await tenantsAPI.create({
          name: formData.name,
          subdomain: formData.subdomain || undefined,
          primary_color: formData.primary_color || undefined,
          secondary_color: formData.secondary_color || undefined,
        });
      } else if (editingTenant) {
        // Only send mod_* keys that differ from current tenant features
        const changedFeatures: Record<string, boolean> = {};
        for (const [k, v] of Object.entries(moduleToggles)) {
          if (k.startsWith('mod_') && (editingTenant.features || {})[k] !== v) {
            changedFeatures[k] = v;
          }
        }
        await tenantsAPI.update(editingTenant.id, {
          name: formData.name,
          subdomain: formData.subdomain || undefined,
          primary_color: formData.primary_color || undefined,
          secondary_color: formData.secondary_color || undefined,
          ...(Object.keys(changedFeatures).length > 0 ? { features: changedFeatures } : {}),
        });
      }
      closeModal();
      fetchTenants();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to save tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTenant) return;
    setDeleting(true);

    try {
      await tenantsAPI.delete(deleteTenant.id);
      setDeleteTenant(null);
      fetchTenants();
    } catch (err: any) {
      const data = err.response?.data;
      const message = typeof data === 'string' ? data
        : data?.detail || data?.message || err.message || 'Failed to delete tenant';
      setError(typeof message === 'string' ? message : 'Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (tenant: Tenant) => {
    const newActive = tenant.is_active === false ? true : false;
    try {
      await tenantsAPI.update(tenant.id, { is_active: newActive });
      fetchTenants();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update tenant status');
    }
    setToggleActiveTenant(null);
  };

  const handleToggleTenant = async (tenantId: number) => {
    if (expandedTenant === tenantId) {
      setExpandedTenant(null);
      return;
    }
    setExpandedTenant(tenantId);
    setMembersLoading(true);
    try {
      const res = await tenantsAPI.getMembers(tenantId);
      setTenantMembers(res.data.members || []);
    } catch (err) {
      console.error('Failed to load tenant members:', err);
      setTenantMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const getRoleBadgeStyle = (role: string, userType: string): React.CSSProperties => {
    if (role === 'admin') return { color: '#f87171', background: 'rgba(248, 113, 113, 0.15)' };
    if (userType === 'trial_member' || userType === 'trial') return { color: '#f6ad55', background: 'rgba(246, 173, 85, 0.15)' };
    if (userType === 'guest') return { color: '#718096', background: 'rgba(113, 128, 150, 0.15)' };
    return { color: '#68d391', background: 'rgba(104, 211, 145, 0.15)' };
  };

  const getRoleLabel = (role: string, userType: string): string => {
    if (role === 'admin') return 'admin';
    if (userType === 'trial_member' || userType === 'trial') return 'trial member';
    if (userType === 'guest') return 'guest';
    return 'member';
  };

  return (
    <div className="admin-tenants-page">
      <div className="admin-page-header">
        <h1>Tenants</h1>
        <button className="btn-primary" onClick={openCreateModal}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Add Tenant
        </button>
      </div>

      {error && (
        <div className="error-message">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 4V9M8 11V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          {error}
        </div>
      )}

      <div className="admin-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="admin-loading-spinner" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path d="M6 42V18L24 6L42 18V42H30V30H18V42H6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            <h3>No tenants found</h3>
            <p>Add your first tenant to get started</p>
          </div>
        ) : (
          <div className="tenant-list">
            {tenants.map((tenant) => {
              const isExpanded = expandedTenant === tenant.id;
              const admins = tenantMembers.filter(m => m.role === 'admin');
              const members = tenantMembers.filter(m => m.role !== 'admin' && m.user_type !== 'guest' && m.user_type !== 'trial_member' && m.user_type !== 'trial');
              const trialMembers = tenantMembers.filter(m => m.user_type === 'trial_member' || m.user_type === 'trial');
              const guests = tenantMembers.filter(m => m.user_type === 'guest');

              return (
                <div key={tenant.id} className="tenant-card" style={{ marginBottom: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
                  {/* Tenant header row */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}
                    onClick={() => handleToggleTenant(tenant.id)}
                  >
                    <svg style={{ flexShrink: 0, transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'var(--text-muted)' }} width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '14px' }}>{tenant.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {tenant.subdomain ? `${tenant.subdomain}.adroitai.ai` : 'No subdomain'}
                        {' \u00B7 '}Created {formatDate(tenant.created_at)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {tenant.primary_color && (
                        <div style={{ width: '20px', height: '20px', borderRadius: '4px', background: tenant.primary_color, border: '1px solid rgba(255,255,255,0.1)' }} title={`Primary: ${tenant.primary_color}`} />
                      )}
                      <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', color: tenant.is_active === false ? '#fc8181' : '#68d391', background: tenant.is_active === false ? 'rgba(252,129,129,0.1)' : 'rgba(104,211,145,0.1)' }}>
                        {tenant.is_active === false ? 'Suspended' : 'Active'}
                      </span>
                      <div className="table-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn"
                          onClick={() => {
                            if (tenant.is_active !== false) {
                              setToggleActiveTenant(tenant);
                            } else {
                              handleToggleActive(tenant);
                            }
                          }}
                          title={tenant.is_active === false ? 'Activate tenant' : 'Deactivate tenant'}
                        >
                          {tenant.is_active === false ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M8 5V11M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          )}
                        </button>
                        <button className="action-btn" onClick={() => navigate(`/admin/tenants/${tenant.id}`)} title="View tenant details">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M1 8C3 4 6 2 8 2C10 2 13 4 15 8C13 12 10 14 8 14C6 14 3 12 1 8Z" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                        </button>
                        <button className="action-btn" onClick={() => openEditModal(tenant)} title="Edit tenant">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button className="action-btn action-btn--danger" onClick={() => setDeleteTenant(tenant)} title="Delete tenant">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 4H13M6 4V3C6 2.44772 6.44772 2 7 2H9C9.55228 2 10 2.44772 10 3V4M12 4V13C12 13.5523 11.5523 14 11 14H5C4.44772 14 4 13.5523 4 13V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded tree view */}
                  {isExpanded && (
                    <div style={{ padding: '0 16px 16px 42px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                      {membersLoading ? (
                        <div style={{ padding: '16px 0', textAlign: 'center' }}>
                          <div className="admin-loading-spinner" style={{ width: 20, height: 20, borderWidth: 2, margin: '0 auto' }} />
                        </div>
                      ) : (
                        <div style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-muted)', paddingTop: '12px' }}>
                          {/* Host/Admin */}
                          {admins.map((admin, i) => (
                            <div key={admin.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0' }}>
                              <span style={{ color: 'var(--text-secondary)' }}>{i === 0 && admins.length === 1 ? '\u251C\u2500\u2500' : i === 0 ? '\u251C\u2500\u2500' : '\u2502  '}</span>
                              <span style={{ color: '#f87171', fontWeight: 600 }}>Host:</span>
                              <span style={{ color: 'var(--text-primary)' }}>{admin.name}</span>
                              <span style={{ ...getRoleBadgeStyle(admin.role, admin.user_type), fontSize: '11px', padding: '1px 6px', borderRadius: '4px' }}>admin</span>
                            </div>
                          ))}

                          {/* Members section */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0', marginTop: '4px' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{'\u251C\u2500\u2500'}</span>
                            <span style={{ color: '#68d391' }}>Members ({members.length})</span>
                          </div>
                          {members.map((member, i) => (
                            <div key={member.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 0', paddingLeft: '24px' }}>
                              <span style={{ color: 'var(--text-secondary)' }}>{i === members.length - 1 ? '\u2514\u2500\u2500' : '\u251C\u2500\u2500'}</span>
                              <span style={{ color: 'var(--text-primary)' }}>{member.name}</span>
                              <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>\u2014 last active: {formatRelativeDate(member.last_seen || member.last_login)}</span>
                              <span style={{ ...getRoleBadgeStyle(member.role, member.user_type), fontSize: '11px', padding: '1px 6px', borderRadius: '4px' }}>{getRoleLabel(member.role, member.user_type)}</span>
                              {member.engagement !== 'none' && (
                                <span style={{ fontSize: '10px', color: member.engagement === 'high' ? '#68d391' : member.engagement === 'medium' ? '#f6ad55' : '#fc8181' }}>
                                  {member.engagement}
                                </span>
                              )}
                            </div>
                          ))}

                          {/* Trial Members section */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0', marginTop: '4px' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{'\u251C\u2500\u2500'}</span>
                            <span style={{ color: '#f6ad55' }}>Trial Members ({trialMembers.length})</span>
                          </div>
                          {trialMembers.map((member, i) => (
                            <div key={member.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 0', paddingLeft: '24px' }}>
                              <span style={{ color: 'var(--text-secondary)' }}>{i === trialMembers.length - 1 ? '\u2514\u2500\u2500' : '\u251C\u2500\u2500'}</span>
                              <span style={{ color: 'var(--text-primary)' }}>{member.name}</span>
                              <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>\u2014 last active: {formatRelativeDate(member.last_seen || member.last_login)}</span>
                              <span style={{ ...getRoleBadgeStyle(member.role, member.user_type), fontSize: '11px', padding: '1px 6px', borderRadius: '4px' }}>trial member</span>
                              {member.days_remaining !== null && (
                                <span style={{ fontSize: '10px', color: member.days_remaining <= 3 ? '#fc8181' : '#f6ad55' }}>
                                  {member.days_remaining}d remaining
                                </span>
                              )}
                            </div>
                          ))}

                          {/* Guests section */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0', marginTop: '4px' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{'\u2514\u2500\u2500'}</span>
                            <span style={{ color: '#718096' }}>Guests ({guests.length})</span>
                          </div>
                          {guests.map((member, i) => (
                            <div key={member.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '3px 0', paddingLeft: '24px' }}>
                              <span style={{ color: 'var(--text-secondary)' }}>{i === guests.length - 1 ? '\u2514\u2500\u2500' : '\u251C\u2500\u2500'}</span>
                              <span style={{ color: 'var(--text-primary)' }}>{member.name}</span>
                              <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>\u2014 last active: {formatRelativeDate(member.last_seen || member.last_login)}</span>
                              <span style={{ ...getRoleBadgeStyle(member.role, member.user_type), fontSize: '11px', padding: '1px 6px', borderRadius: '4px' }}>guest</span>
                            </div>
                          ))}

                          {tenantMembers.length === 0 && (
                            <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', padding: '4px 0' }}>No members in this tenant</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? 'Add Tenant' : 'Edit Tenant'}</h3>
              <button className="modal-close" onClick={closeModal}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {formError && (
                  <div className="error-message">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 4V9M8 11V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    {formError}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Company Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Acme Pharma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Subdomain</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="acme"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Will be accessible at: {formData.subdomain || 'subdomain'}.adroitai.ai
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Website URL (optional, for brand harvesting)</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="https://acmepharma.com"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={handleHarvestColors}
                      disabled={harvesting}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {harvesting ? 'Harvesting...' : 'Harvest Colors'}
                    </button>
                  </div>
                </div>

                {harvestedColors && (
                  <div className="form-group" style={{ padding: '12px', background: 'var(--hover-bg)', borderRadius: '8px' }}>
                    <label className="form-label" style={{ marginBottom: '12px' }}>Harvested Colors (click to select)</label>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {Object.entries(harvestedColors).filter(([key]) => key !== 'logo_url').map(([key, color]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setFormData({ ...formData, primary_color: color })}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '8px',
                            background: color,
                            border: formData.primary_color === color ? '3px solid #111' : '2px solid rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                          }}
                          title={`${key}: ${color}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Primary Color</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Secondary Color</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        style={{ width: '40px', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>

                {modalMode === 'edit' && (
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label" style={{ marginBottom: '12px' }}>Module Visibility</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {([
                        ['mod_registry', 'Registry'],
                        ['mod_targets', 'Targets'],
                        ['mod_batches', 'Batches'],
                        ['mod_assays', 'Assays & Biology'],
                        ['mod_pipeline', 'Pipeline'],
                        ['mod_eln', 'ELN'],
                        ['mod_genomics', 'Genomics & Omics'],
                        ['mod_biologics', 'Biologics'],
                        ['mod_pharmacology', 'Pharmacology'],
                        ['mod_clinical', 'Clinical'],
                        ['mod_computational', 'Computational'],
                        ['mod_admet', 'ADMET & Safety'],
                      ] as const).map(([key, label]) => {
                        const enabled = moduleToggles[key] !== false;
                        return (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{label}</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              onClick={() => setModuleToggles((prev) => ({ ...prev, [key]: !enabled }))}
                              style={{
                                position: 'relative',
                                display: 'inline-flex',
                                height: '22px',
                                width: '40px',
                                alignItems: 'center',
                                borderRadius: '11px',
                                border: 'none',
                                background: enabled ? '#0D9488' : 'rgba(128,128,128,0.3)',
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                padding: 0,
                              }}
                            >
                              <span style={{
                                display: 'inline-block',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                background: '#fff',
                                transition: 'transform 0.15s',
                                transform: enabled ? 'translateX(20px)' : 'translateX(3px)',
                              }} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : modalMode === 'create' ? 'Create Tenant' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTenant && (
        <div className="modal-overlay" onClick={() => setDeleteTenant(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Tenant</h3>
              <button className="modal-close" onClick={() => setDeleteTenant(null)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteTenant.name}</strong>? This will also remove all associated data. This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDeleteTenant(null)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ background: '#dc2626' }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Activate/Deactivate Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!toggleActiveTenant}
        onClose={() => setToggleActiveTenant(null)}
        onConfirm={() => toggleActiveTenant && handleToggleActive(toggleActiveTenant)}
        title={`Deactivate ${toggleActiveTenant?.name}?`}
        description="Members will lose access until reactivated."
        confirmLabel="Deactivate"
        confirmColor="red"
      />
    </div>
  );
};

export default TenantsPage;
